import React, { useState } from 'react';
import { Language, TranslationContent } from '../types';

interface ContactProps {
  t: TranslationContent['contact'];
  lang: Language;
}

const Contact: React.FC<ContactProps> = ({ t, lang }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [popup, setPopup] = useState<{ show: boolean; message: string; type: 'error' | 'success' }>({
    show: false,
    message: '',
    type: 'success'
  });

  const closePopup = () => setPopup({ ...popup, show: false });

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const encode = (data: any) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const message = formData.get('message') as string;
    const botField = formData.get('bot-field') as string;

    if (!name.trim() || !email.trim() || !message.trim()) {
      setPopup({
        show: true,
        message: t.form.validationError,
        type: 'error'
      });
      return;
    }

    if (!validateEmail(email)) {
      setPopup({
        show: true,
        message: t.form.invalidEmail,
        type: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "contact",
          "name": name,
          "email": email,
          "message": message,
          "bot-field": botField
        }),
      });

      if (response.ok) {
        setPopup({
          show: true,
          message: t.form.success,
          type: 'success'
        });
        form.reset();
      } else {
        throw new Error("Netlify Submission Error");
      }
    } catch (error) {
      setPopup({
        show: true,
        message: t.form.error,
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-white scroll-mt-20 relative">
      {popup.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-slate-100 transform animate-in zoom-in-95 duration-300">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
              popup.type === 'success' ? 'bg-green-50 text-green-500' : 'bg-red-50 text-red-500'
            }`}>
              {popup.type === 'success' ? (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <h3 className="text-xl font-bold text-slate-900 text-center mb-2">
              {popup.type === 'success' ? t.form.successTitle : t.form.errorTitle}
            </h3>
            <p className="text-slate-600 text-center mb-8 leading-relaxed">
              {popup.message}
            </p>
            <button 
              onClick={closePopup}
              className="w-full py-4 bg-slate-900 text-white font-bold rounded-2xl hover:bg-slate-800 transition-all active:scale-95"
            >
              {t.form.close}
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-8">{t.title}</h2>
              <p className="text-lg text-slate-600 mb-12 leading-relaxed">
                {t.subtitle}
              </p>
              
              <div className="space-y-8">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-3">{t.emailLabel}</p>
                  {!showEmail ? (
                    <button 
                      onClick={() => setShowEmail(true)}
                      className="inline-flex items-center space-x-2 px-6 py-3 bg-indigo-50 text-indigo-700 font-bold rounded-xl border border-indigo-100 hover:bg-indigo-100 transition-all group"
                    >
                      <svg className="w-4 h-4 text-indigo-500 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>{t.revealEmail}</span>
                    </button>
                  ) : (
                    <a 
                      href={`mailto:${t.email}`} 
                      className="text-2xl font-bold text-indigo-600 hover:text-indigo-700 transition-colors animate-in fade-in slide-in-from-left-2 duration-300"
                    >
                      {t.email}
                    </a>
                  )}
                </div>
                <div className="p-8 bg-slate-50 rounded-3xl border border-slate-200 italic text-slate-600 text-sm md:text-base leading-relaxed relative">
                  <span className="absolute top-4 left-4 text-indigo-200 text-4xl font-serif">"</span>
                  <p className="relative z-10 px-2">{t.quote}</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-8 md:p-10 rounded-[2.5rem] border border-slate-200 shadow-sm">
              <form 
                name="contact" 
                method="POST" 
                onSubmit={handleSubmit} 
                noValidate
                className="space-y-6"
              >
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>Don’t fill this out if you’re human: <input name="bot-field" aria-hidden="true" /></label>
                </p>
                
                <div>
                  <label className="block text-sm font-bold text-slate-800 mb-2">{t.form.name} *</label>
                  <input 
                    name="name"
                    type="text" 
                    required
                    placeholder={lang === 'en' ? "Your name" : "Seu nome"}
                    className="w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-800 mb-2">{t.form.email} *</label>
                  <input 
                    name="email"
                    type="email" 
                    required
                    placeholder="email@exemplo.com"
                    className="w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-800 mb-2">{t.form.message} *</label>
                  <textarea 
                    name="message"
                    required
                    rows={4} 
                    placeholder={t.form.message}
                    className="w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all resize-none placeholder:text-slate-500"
                  ></textarea>
                </div>
                
                <button 
                  disabled={isSubmitting}
                  type="submit" 
                  className="w-full py-5 px-8 bg-indigo-600 hover:bg-indigo-700 shadow-xl shadow-indigo-100 rounded-2xl font-extrabold text-white transition-all transform active:scale-[0.97]"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t.form.sending}
                    </span>
                  ) : (
                    t.form.submit
                  )}
                </button>
                <p className="text-[10px] text-slate-600 text-center uppercase tracking-widest font-extrabold">
                  * {t.form.requiredFields}
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;